/**
 * AI:
 * Status: "in progress"
 * Type: Service
 * SubType: WidgetConfig
 * Reason: Live preview of a QL-Widget-Builder definition - widget and raw-data table come from
 *   UiQlWidgetDataBuilder (dataSource ql, dataProvider list), status bar reads the loaded DtoList
 */
import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {forkJoin, Observable, of, ReplaySubject, Subscription} from "rxjs";
import {catchError, debounceTime, map, switchMap, tap} from "rxjs/operators";
import {QlQueryDto} from "../../../../shared/ql/data/dto/ql-query.dto";
import {QlBuilderModel} from "../../../../shared/ql/builder/ql-builder-model";
import {QlBuilderQueryConverter} from "../../../../shared/ql/builder/ql-builder-query-converter";
import {QlBuilderViolation} from "../../../../shared/ql/builder/ql-builder-violation";
import {QlBuilderKeyLabels} from "../../../../shared/ql/component/ql-builder/logic/ql-builder-key-labels";
import {DtoList} from "../../../../shared/dto/dto.list";
import {QlPipeResultMetricsDto} from "../../../../shared/ql/data/dto/ql-pipe-result-metrics.dto";
import {CoreMetaExternalSideCarDto} from "../../../../shared/meta/dto/meta-external-side-car.dto";
import {CoreMetaExternalSideCarService} from "../../../../shared/meta/service/core-meta-external-side-car.service";
import {MetaService} from "../../../../shared/meta/service/meta.service";
import {MetaDataEntityDto} from "../../../../shared/meta/dto/meta-data-entity.dto";
import {UiQlWidgetMapping, UiQlWidgetType} from "../../../model/dto/api/ui-ql-widget-mapping.dto";
import {UiQlWidgetDataBuilder} from "../../../service/domain/ui-ql-widget-data-builder.service";
import {UiQlWidgetPreviewState} from "../../../interface/ui-ql-widget-preview-state.interface";
import {UiQlWidgetPreviewStatus} from "../../../interface/ui-ql-widget-preview-status.interface";
import {UiQlWidgetPreviewEvaluationInput} from "../../../interface/ui-ql-widget-preview-evaluation-input.interface";
import {UiQlWidgetPreviewTab} from "../../../interface/ui-ql-widget-preview-tab.interface";
import {uiQlViolationText} from "../../../logic/ui-ql-violation-texts";

/**
 * Live preview of a QL-Widget-Builder definition (requirement.md Frontend "Vorschau"): tab
 * "Widget" renders the configured widget type, tab "Rohdaten" a plain table over the SAME
 * definition — both built by {@link UiQlWidgetDataBuilder}, the builder the Alpha Home tile uses.
 *
 * Pipeline per input change: debounce 500 ms → client policy pre-check
 * ({@code QlBuilderQueryConverter.precheckQlQuery}) plus any server verdict handed in → on a
 * violation NOTHING is executed and the violations are shown → otherwise side-car discovery (only
 * when needed for the uniqueness hint) → entity metadata of the definition's aliases (cached by
 * {@code MetaService}; only for the German column/legend labels, a failed load costs nothing but
 * the labels) → build. {@code switchMap} drops an evaluation that a newer input overtook,
 * including its in-flight discovery/metadata calls; the widget itself only mounts for the winning
 * evaluation, so a stale definition never reaches {@code /core/ql/query}.
 *
 * An input change that does not change anything relevant (same definition, type, mapping,
 * verdict — e.g. the host re-emitting its snapshot) is ignored: the mounted widget stays, no
 * query is repeated. While an evaluation is pending the previous builds are dropped, so nothing
 * of an older definition is rendered or reported until the new one is ready.
 *
 * Declared and exported by `UiQlWidgetBuilderModule` (NgModule, fe_core_standards): hosts are
 * {@code UiQlWidgetWorkbenchPage} and the widget-test-shell harness page, which imports that module.
 */
@Component({
    selector: 'ui-ql-widget-preview',
    templateUrl: './ui-ql-widget-preview.component.html',
    styleUrls: ['./ui-ql-widget-preview.component.scss'],
    standalone: false,
})
export class UiQlWidgetPreviewComponent implements OnInit, OnChanges, OnDestroy {

    static readonly DEBOUNCE_MS = 500;

    /** Metadata rounds per evaluation — one per join level (root, joins, nested joins, ...). */
    static readonly MAX_META_ROUNDS = 4;

    /** Builder tree — preferred source; converted with {@code QlBuilderQueryConverter.toQlQuery}. */
    @Input() model: QlBuilderModel | null = null;

    /** Raw definition (e.g. the Playground JSON tab) — used when no {@link model} is set. */
    @Input() qlQuery: QlQueryDto | null = null;

    /** Widget type — switchable live from outside. */
    @Input() widgetType: UiQlWidgetType = 'table';

    /** Type-dependent mapping; `{}` for table/list. */
    @Input() mapping: UiQlWidgetMapping = {};

    /**
     * Side-car discovery rows of the host, when the caller already has them. Absent and the
     * definition joins a side-car on its start entity → the preview asks the discovery itself.
     */
    @Input() sideCars?: CoreMetaExternalSideCarDto[];

    /**
     * Server policy verdict for the current definition (`/ui/qlWidgets/validate`), if the host
     * runs one. Non-empty blocks execution exactly like a client pre-check violation.
     */
    @Input() policyViolations: QlBuilderViolation[] | null = null;

    /** Every new evaluation result (phase, violations, build results) — for a Playground inspector. */
    @Output() stateChange = new EventEmitter<UiQlWidgetPreviewState>();

    /** Status of the widget tab after each load (rows, total, runtime, pipe errors). */
    @Output() statusChange = new EventEmitter<UiQlWidgetPreviewStatus>();

    state: UiQlWidgetPreviewState = UiQlWidgetPreviewComponent.emptyState();

    activeTab: UiQlWidgetPreviewTab = 'widget';

    /** Status per tab; `null` while the widget of that tab has not reported data yet. */
    status: { [tab in UiQlWidgetPreviewTab]: UiQlWidgetPreviewStatus | null } = {widget: null, raw: null};

    /**
     * Input snapshots. A ReplaySubject(1) because Angular fires the first ngOnChanges BEFORE
     * ngOnInit subscribes — the first snapshot must not be lost.
     */
    private readonly input$ = new ReplaySubject<UiQlWidgetPreviewEvaluationInput>(1);
    private subscription: Subscription | null = null;

    /** `performance.now()` when the widgets of the current evaluation were handed out. */
    private builtAt: number | null = null;

    /** Fingerprint of the last snapshot handed to the pipeline — an identical one is ignored. */
    private lastFingerprint: string | null = null;

    constructor(
        private dataBuilder: UiQlWidgetDataBuilder,
        private sideCarService: CoreMetaExternalSideCarService,
        private metaService: MetaService) {
    }

    ngOnInit(): void {
        this.subscription = this.input$.pipe(
            tap(() => this.markPending()),
            debounceTime(UiQlWidgetPreviewComponent.DEBOUNCE_MS),
            switchMap(input => this.evaluate(input)),
        ).subscribe(state => this.apply(state));
    }

    ngOnChanges(changes: SimpleChanges): void {

        const input: UiQlWidgetPreviewEvaluationInput = {
            model: this.model,
            qlQuery: this.qlQuery,
            widgetType: this.widgetType,
            mapping: this.mapping ?? {},
            sideCars: this.sideCars,
            policyViolations: this.policyViolations,
        };

        // a pure re-emission of the same inputs must not unmount the widget and repeat its query
        const fingerprint = UiQlWidgetPreviewComponent.fingerprint(input);
        if (fingerprint === this.lastFingerprint) {
            return;
        }
        this.lastFingerprint = fingerprint;

        this.input$.next(input);
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
        this.input$.complete();
    }

    /** German text of a violation (fixed text per code, never the raw server message). */
    violationText(violation: QlBuilderViolation): string {
        return uiQlViolationText(violation);
    }

    selectTab(tab: UiQlWidgetPreviewTab | string | number | undefined): void {
        this.activeTab = tab === 'raw' ? 'raw' : 'widget';
    }

    /** `(onLoadData)` of a preview widget — derives the status bar from what came back. */
    onWidgetLoaded(tab: UiQlWidgetPreviewTab, dtoList: DtoList): void {

        const build = tab === 'widget' ? this.state.widget : this.state.raw;
        const metrics = dtoList?.metrics as QlPipeResultMetricsDto;

        let runtimeMs: number | null = null;
        let runtimeSource: UiQlWidgetPreviewStatus['runtimeSource'] = null;

        if (dtoList?.performance?.totalMs != null) {
            runtimeMs = dtoList.performance.totalMs;
            runtimeSource = 'engine';
        } else if (metrics?.hasRuntimeMetrics && metrics.metricRuntimeMs != null) {
            runtimeMs = metrics.metricRuntimeMs;
            runtimeSource = 'pipe';
        } else if (this.builtAt != null) {
            runtimeMs = Math.round(performance.now() - this.builtAt);
            runtimeSource = 'client';
        }

        const status: UiQlWidgetPreviewStatus = {
            rows: dtoList?.entries?.length ?? 0,
            total: build?.countAvailable ? (dtoList?.pagingResponse?.count ?? null) : null,
            runtimeMs,
            runtimeSource,
            pipeErrors: (dtoList?.messages ?? []).map(message => message.content).filter(content => !!content),
        };

        this.status = {...this.status, [tab]: status};

        if (tab === 'widget') {
            this.statusChange.emit(status);
        }
    }

    /** Status of the visible tab. */
    /**
     * Q3-10: a bar/line/area chart gets the full pane width. The shared chart widget sizes its canvas to 40 %
     * of the container at a 2:1 ratio - in the preview pane a ~320 x 160 px chart whose bars were ~40 px high.
     * At full width it is ≥ 300 px high on a 1280 px screen. Pie charts (1:1) and the category donut keep their size.
     */
    get wideChart(): boolean {
        return this.widgetType === 'chart' && (this.mapping as { chartType?: string })?.chartType !== 'pie';
    }

    get currentStatus(): UiQlWidgetPreviewStatus | null {
        return this.status[this.activeTab];
    }

    /** Current build result of the visible tab. */
    get currentBuild() {
        return this.activeTab === 'raw' ? this.state.raw : this.state.widget;
    }

    // ------------------------------------------------------------------
    // pipeline
    // ------------------------------------------------------------------

    /**
     * One evaluation. Returns synchronously (`of`) wherever no HTTP is involved, so the debounce is
     * the only delay; the side-car discovery is the one call {@code switchMap} can cancel.
     */
    private evaluate(input: UiQlWidgetPreviewEvaluationInput): Observable<UiQlWidgetPreviewState> {

        let qlQuery: QlQueryDto | null;
        try {
            qlQuery = input.model ? QlBuilderQueryConverter.toQlQuery(input.model) : input.qlQuery;
        } catch (error) {
            return of({
                ...UiQlWidgetPreviewComponent.emptyState(),
                phase: 'error',
                errorMessage: 'Die Abfrage konnte nicht aus dem Builder erzeugt werden.',
            });
        }

        if (!qlQuery?.start?.name) {
            return of(UiQlWidgetPreviewComponent.emptyState());
        }

        const violations = [
            ...QlBuilderQueryConverter.precheckQlQuery(qlQuery, input.model),
            ...(input.policyViolations ?? []),
        ];
        if (violations.length) {
            return of({...UiQlWidgetPreviewComponent.emptyState(), phase: 'violations', qlQuery, violations});
        }

        return this.resolveSideCars(qlQuery, input.sideCars).pipe(
            switchMap(sideCars => this.resolveLabels(input.model ?? UiQlWidgetDataBuilder.modelOf(qlQuery)).pipe(
                map(labels => this.build(qlQuery, input, sideCars, labels)),
            )),
        );
    }

    /**
     * German labels per result key from the entity metadata of every alias of the definition
     * ({@code QlBuilderKeyLabels}): the root first, then each join level once its parent's
     * metadata names the target entity. {@code MetaService} caches per alias, so the calls are
     * free after the builder loaded them. An alias that fails to load is skipped — its keys keep
     * the engine's labels — and never retried within the evaluation. {@code null} without a model.
     */
    private resolveLabels(model: QlBuilderModel | null): Observable<ReadonlyMap<string, string> | null> {

        if (!model?.root?.entityName) {
            return of(null);
        }

        const metas = new Map<string, MetaDataEntityDto>();
        const failed = new Set<string>();

        const round = (n: number): Observable<Map<string, MetaDataEntityDto>> => {
            const missing = QlBuilderKeyLabels.entityAliases(model, metas)
                .filter(alias => !metas.has(alias) && !failed.has(alias));
            if (!missing.length || n >= UiQlWidgetPreviewComponent.MAX_META_ROUNDS) {
                return of(metas);
            }
            return forkJoin(missing.map(alias =>
                this.metaService.getByAlias(alias, true, true, true, true, true).pipe(
                    catchError(() => of(null as MetaDataEntityDto | null)),
                ))).pipe(
                switchMap(loaded => {
                    loaded.forEach((meta, index) => meta ? metas.set(missing[index], meta) : failed.add(missing[index]));
                    return round(n + 1);
                }),
            );
        };

        return round(0).pipe(map(loaded => UiQlWidgetPreviewComponent.metadataLabels(model, loaded)));
    }

    /**
     * Only the labels the metadata actually improved. Without metadata {@code QlBuilderKeyLabels}
     * falls back to the raw member name ("status"), which must never replace the engine's own form
     * label ("Status") — so every key whose label equals the no-metadata baseline is left out, and
     * with no metadata at all the result is {@code null}.
     */
    private static metadataLabels(
        model: QlBuilderModel,
        metas: ReadonlyMap<string, MetaDataEntityDto>): ReadonlyMap<string, string> | null {

        if (!metas.size) {
            return null;
        }

        const baseline = QlBuilderKeyLabels.labels(model, new Map());
        const improved = new Map<string, string>();
        QlBuilderKeyLabels.labels(model, metas).forEach((label, key) => {
            if (label && label !== baseline.get(key)) {
                improved.set(key, label);
            }
        });

        return improved.size ? improved : null;
    }

    private resolveSideCars(
        qlQuery: QlQueryDto,
        given: CoreMetaExternalSideCarDto[] | undefined): Observable<CoreMetaExternalSideCarDto[] | undefined> {

        if (given) {
            return of(given);
        }

        const rootHasSideCar = (qlQuery.start.joins ?? [])
            .some(join => !!join.joinParameters && Object.keys(join.joinParameters).length > 0);
        if (!rootHasSideCar) {
            return of(undefined);
        }

        // Only the uniqueness HINT depends on this; a failed discovery must not block the preview.
        return this.sideCarService.getSideCarsForHost(qlQuery.start.name).pipe(
            catchError(() => of(undefined)),
        );
    }

    private build(
        qlQuery: QlQueryDto,
        input: UiQlWidgetPreviewEvaluationInput,
        sideCars: CoreMetaExternalSideCarDto[] | undefined,
        labels: ReadonlyMap<string, string> | null): UiQlWidgetPreviewState {

        const widget = this.dataBuilder.build({
            qlQuery,
            widgetType: input.widgetType,
            mapping: input.mapping,
            mode: 'preview',
            alias: `ui.ql.preview.${input.widgetType}.widget`,
            name: 'Vorschau',
            sideCars,
            model: input.model,
            labels,
        });

        if (widget.errors.length) {
            return {...UiQlWidgetPreviewComponent.emptyState(), phase: 'invalid', qlQuery, widget};
        }

        const raw = this.dataBuilder.build({
            qlQuery,
            widgetType: 'table',
            mapping: {},
            mode: 'preview',
            alias: 'ui.ql.preview.raw.table',
            name: 'Rohdaten',
            sideCars,
            model: input.model,
            labels,
        });

        return {...UiQlWidgetPreviewComponent.emptyState(), phase: 'ready', qlQuery, widget, raw};
    }

    /**
     * A new input is on its way: nothing of the previous evaluation may be rendered or reported
     * any more — the builds are dropped together with the phase, so neither the template nor a
     * `stateChange` consumer can act on a widget of an older definition.
     */
    private markPending(): void {
        if (this.state.phase !== 'pending' || this.state.widget || this.state.raw) {
            this.state = {...this.state, phase: 'pending', widget: null, raw: null};
        }
    }

    /** What decides an evaluation — every input, structurally. */
    private static fingerprint(input: UiQlWidgetPreviewEvaluationInput): string {
        return JSON.stringify({
            model: input.model ?? null,
            qlQuery: input.qlQuery ?? null,
            widgetType: input.widgetType,
            mapping: input.mapping ?? {},
            sideCars: input.sideCars?.map(sideCar => [sideCar.instanceAlias, sideCar.uniqueGuaranteed]) ?? null,
            policyViolations: input.policyViolations ?? null,
        });
    }

    private apply(state: UiQlWidgetPreviewState): void {
        this.state = state;
        this.status = {widget: null, raw: null};
        this.builtAt = state.phase === 'ready' ? performance.now() : null;
        this.stateChange.emit(state);
    }

    private static emptyState(): UiQlWidgetPreviewState {
        return {phase: 'empty', qlQuery: null, violations: [], widget: null, raw: null, errorMessage: null};
    }
}
